import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Save, Wallet, RefreshCw, AlertCircle } from 'lucide-react';
import AdminNavbar from '../components/AdminNavbar';

const AdminWalletSettings = () => {
    const [wallets, setWallets] = useState({
        ETH: '',
        BSC: '',
        TRX: '',
        SOL: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/settings/wallet');
            if (response.data.walletSettings) {
                setWallets(prev => ({ ...prev, ...response.data.walletSettings }));
            }
        } catch (error) {
            console.error('Failed to fetch wallet settings:', error);
            toast.error('Failed to load wallet settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.put('/admin/settings/wallet', { wallets });
            toast.success('Wallet settings saved successfully!');
        } catch (error) {
            console.error('Failed to save wallet settings:', error);
            toast.error('Failed to save wallet settings');
        } finally {
            setSaving(false);
        }
    };

    const networkInfo = {
        ETH: { name: 'Ethereum', color: 'blue', placeholder: '0x...' },
        BSC: { name: 'BNB Smart Chain', color: 'yellow', placeholder: '0x...' },
        TRX: { name: 'TRON', color: 'red', placeholder: 'T...' },
        SOL: { name: 'Solana', color: 'purple', placeholder: '...' }
    };

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            <div className="container mx-auto px-6 py-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                            <Wallet className="text-blue-500" />
                            Wallet Settings
                        </h1>
                        <p className="text-gray-400 mt-1">Configure receiving wallet addresses for each network</p>
                    </div>

                    <button
                        onClick={fetchSettings}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-gray-300 rounded-lg hover:bg-slate-600 transition-colors"
                    >
                        <RefreshCw size={18} />
                        Refresh
                    </button>
                </div>

                {/* Info Card */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
                    <AlertCircle className="text-blue-400 mt-0.5" size={20} />
                    <div>
                        <p className="text-blue-300 font-medium">What are these addresses for?</p>
                        <p className="text-blue-200/70 text-sm mt-1">
                            These are the admin wallet addresses where user deposits will be swept after detection.
                            Each network requires a separate receiving address.
                        </p>
                    </div>
                </div>

                {/* Wallet Settings Form */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-white mb-6">Receiving Wallet Addresses</h2>

                        <div className="grid gap-6">
                            {Object.entries(networkInfo).map(([network, info]) => (
                                <div key={network} className="bg-slate-700/50 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        {info.name} ({network}) Address
                                    </label>
                                    <input
                                        type="text"
                                        value={wallets[network] || ''}
                                        onChange={(e) => setWallets(prev => ({ ...prev, [network]: e.target.value }))}
                                        placeholder={`Enter ${info.name} wallet address (${info.placeholder})`}
                                        className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Save Button */}
                        <div className="mt-8 flex justify-end">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {saving ? (
                                    <>
                                        <RefreshCw size={18} className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        Save Settings
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Gas Tank Info */}
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mt-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Gas Tank Configuration</h2>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                        <p className="text-yellow-300 text-sm">
                            <strong>Note:</strong> The Gas Tank private key is configured in the server's .env file for security.
                            Contact your administrator to update the <code className="bg-slate-700 px-1 rounded">GAS_TANK_PRIVATE_KEY</code> variable.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminWalletSettings;
